import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../assets/logo.png";
import { INVOICE_BRAND } from "../config/invoiceBrand";

export const generateInvoicePDF = async (invoice) => {
  if (!invoice) return;

  const doc = new jsPDF();
  const PAGE_WIDTH = doc.internal.pageSize.getWidth();
  const PAGE_HEIGHT = doc.internal.pageSize.getHeight();
  const MARGIN = 14;
  const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
  const palette = INVOICE_BRAND.palette || {};

  const toRgb = (hex, fallback) => {
    const safeHex = String(hex || "").replace("#", "");
    if (!/^[\da-fA-F]{6}$/.test(safeHex)) return fallback;
    return [
      parseInt(safeHex.slice(0, 2), 16),
      parseInt(safeHex.slice(2, 4), 16),
      parseInt(safeHex.slice(4, 6), 16)
    ];
  };

  const primary = toRgb(palette.primary, [0, 74, 118]);
  const primaryDark = toRgb(palette.primaryDark, [0, 58, 93]);
  const accent = toRgb(palette.accent, [237, 28, 36]);
  const slate = [51, 65, 85];
  const border = [226, 232, 240];

  const format = (v) =>
    `R ${Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  const formatMeasure = (v, unit) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return "-";
    return `${n.toFixed(2)} ${unit}`;
  };

  const issueDate = new Date(invoice.date || new Date());
  const dueDate = new Date(issueDate);
  dueDate.setDate(dueDate.getDate() + (INVOICE_BRAND.validityDays || 7));

  const pageBottom = PAGE_HEIGHT - 20;
  const ensureSpace = (y, needed = 16) => {
    if (y + needed <= pageBottom) return y;
    doc.addPage();
    return 20;
  };

  const drawSectionTitle = (title, y) => {
    const nextY = ensureSpace(y, 10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...primaryDark);
    doc.text(title, MARGIN, nextY);
    doc.setDrawColor(...border);
    doc.line(MARGIN, nextY + 2, PAGE_WIDTH - MARGIN, nextY + 2);
    return nextY + 8;
  };

  const loadImageAsDataUrl = async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Image fetch failed");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  /* ===== Minimal header ===== */
  doc.setFillColor(...primaryDark);
  doc.rect(0, 0, PAGE_WIDTH, 4, "F");

  let headerY = 10;
  let logoRightX = MARGIN + 30;
  try {
    const logoData = await loadImageAsDataUrl(logo);
    const logoProps = doc.getImageProperties(logoData);
    const h = 12;
    const w = Math.max(28, (logoProps.width / logoProps.height) * h);
    doc.addImage(logoData, "PNG", MARGIN, headerY, w, h);
    logoRightX = MARGIN + w + 6;
  } catch {
    // no-op
  }

  const rightMetaX = PAGE_WIDTH - MARGIN;
  const leftHeaderMaxWidth = Math.max(50, rightMetaX - logoRightX - 70);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...primaryDark);
  const companyLines = doc.splitTextToSize(INVOICE_BRAND.companyName || "-", leftHeaderMaxWidth);
  doc.text(companyLines, logoRightX, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  const tradingLines = doc.splitTextToSize(INVOICE_BRAND.tradingName || "-", leftHeaderMaxWidth);
  doc.text(tradingLines, logoRightX, 19);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("QUOTATION", rightMetaX, 13, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text(`Quote #: ${invoice.invoiceNumber || "N/A"}`, rightMetaX, 18, { align: "right" });
  doc.text(`Issue: ${issueDate.toLocaleDateString()}`, rightMetaX, 22, { align: "right" });
  doc.text(`Valid: ${dueDate.toLocaleDateString()}`, rightMetaX, 26, { align: "right" });

  /* ===== Executive summary cards ===== */
  const risk = invoice.riskAssessment || invoice.risk || {};
  const paymentStatus = String(invoice.paymentStatus || "unpaid").toUpperCase();
  const cardY = 31;
  const cardW = (CONTENT_WIDTH - 8) / 3;
  const cards = [
    ["Total Quotation", format(invoice.total)],
    ["Risk Level", (risk.riskLevel || "UNKNOWN").toUpperCase()],
    ["Payment Status", paymentStatus]
  ];
  cards.forEach((card, idx) => {
    const x = MARGIN + idx * (cardW + 4);
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(...border);
    doc.roundedRect(x, cardY, cardW, 14, 2, 2, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...slate);
    doc.text(card[0], x + 3, cardY + 5);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...primaryDark);
    doc.text(card[1], x + 3, cardY + 10.5);
  });

  const client = invoice.client || {};
  const fromBody = [
    ["Business", INVOICE_BRAND.companyName || "-"],
    ["VAT", INVOICE_BRAND.vatNumber || "-"],
    ["Tax Code", INVOICE_BRAND.taxCode || "-"],
    ["Address", (INVOICE_BRAND.addressLines || [INVOICE_BRAND.address]).join(", ")],
    ["Email", INVOICE_BRAND.contactEmail || "-"]
  ];
  const toBody = [
    ["Client", client.name || "Client Name"],
    ["Company", client.company || "-"],
    ["Email", client.email || client.contact || "-"],
    ["Phone", client.phone || "-"]
  ];

  let currentY = 52;
  currentY = drawSectionTitle("Issuer Details", currentY);
  autoTable(doc, {
    startY: currentY,
    body: fromBody,
    tableWidth: CONTENT_WIDTH,
    margin: { left: MARGIN },
    theme: "grid",
    styles: { fontSize: 8.5, cellPadding: 2.6, textColor: [15, 23, 42] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 30, fillColor: [248, 250, 252] }
    }
  });
  currentY = doc.lastAutoTable.finalY + 8;

  currentY = drawSectionTitle("Client Details", currentY);
  autoTable(doc, {
    startY: currentY,
    body: toBody,
    tableWidth: CONTENT_WIDTH,
    margin: { left: MARGIN },
    theme: "grid",
    styles: { fontSize: 8.5, cellPadding: 2.6, textColor: [15, 23, 42] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 26, fillColor: [248, 250, 252] }
    }
  });

  currentY = doc.lastAutoTable.finalY + 8;
  currentY = drawSectionTitle("Route & Load", currentY);
  const route = invoice.route || {};
  const load = invoice.loadDetails || {};
  autoTable(doc, {
    startY: currentY,
    body: [
      ["From", route.from || "-"],
      ["To", route.to || "-"],
      ["Distance", `${Math.round(Number(route.distance || 0))} km`],
      ["Effective Distance", `${Number(invoice?.pricing?.effectiveDistanceKm || route.distance || 0).toFixed(1)} km`],
      ["Weight", formatMeasure(load.weight, "t")],
      ["Width", formatMeasure(load.width, "m")],
      ["Height", formatMeasure(load.height, "m")],
      ["Machine Type", load.machineType || "-"],
      ["Machine Category", load.machineCategory || "-"],
      ["Trailer Type", load.trailerType || "-"],
      ["Axle Configuration", load.axleConfiguration || "-"]
    ],
    tableWidth: CONTENT_WIDTH,
    theme: "grid",
    styles: { fontSize: 8.7, cellPadding: 2.6, textColor: [15, 23, 42] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 38, fillColor: [248, 250, 252] }
    }
  });
  currentY = doc.lastAutoTable.finalY + 8;

  if (invoice.routeImageSrc) {
    try {
      currentY = drawSectionTitle("Route Preview", currentY);
      currentY = ensureSpace(currentY, 50);
      const routeImg = await loadImageAsDataUrl(invoice.routeImageSrc);
      doc.addImage(routeImg, "PNG", MARGIN, currentY, CONTENT_WIDTH, 45);
      currentY += 50;
    } catch {
      // continue without image
    }
  }

  currentY = drawSectionTitle("Financial Breakdown", currentY);
  let breakdown = [];
  if (Array.isArray(invoice.breakdown)) {
    breakdown = invoice.breakdown;
  } else if (typeof invoice.breakdown === "object") {
    breakdown = [
      { label: "Fuel Cost", value: invoice.breakdown.fuelCost },
      { label: "Transport Cost", value: invoice.breakdown.transportCost },
      { label: "Toll Cost", value: invoice.breakdown.tollCost },
      { label: "Escort Cost", value: invoice.breakdown.escortCost }
    ];
  }
  const tableData = breakdown.map((item) => [item.label, format(item.value)]);
  tableData.push(["TOTAL QUOTATION", format(invoice.total)]);

  autoTable(doc, {
    startY: currentY,
    head: [["Item", "Amount"]],
    body: tableData,
    tableWidth: CONTENT_WIDTH,
    theme: "grid",
    styles: { fontSize: 8.8, cellPadding: 2.8, textColor: [15, 23, 42] },
    headStyles: { fillColor: primary, textColor: [255, 255, 255] },
    didParseCell: (hook) => {
      const row = hook.row;
      if (!row) return;
      const isTotal = row.raw?.[0] === "TOTAL QUOTATION";
      if (isTotal) {
        hook.cell.styles.fillColor = [241, 245, 249];
        hook.cell.styles.fontStyle = "bold";
        hook.cell.styles.textColor = [0, 58, 93];
      }
    }
  });
  currentY = doc.lastAutoTable.finalY + 8;

  currentY = drawSectionTitle("Risk & Compliance", currentY);
  autoTable(doc, {
    startY: currentY,
    body: [
      ["Classification", risk.classification || load.classification || "Unknown"],
      ["Risk Level", (risk.riskLevel || "unknown").toUpperCase()],
      ["Risk Score", String(Number(risk.score || 0))],
      ["Permit Required", invoice?.compliance?.requiresPermit ? "Yes" : "No"],
      ["Police Escort", invoice?.compliance?.policeEscort ? "Yes" : "No"]
    ],
    tableWidth: CONTENT_WIDTH,
    theme: "grid",
    styles: { fontSize: 8.6, cellPadding: 2.6, textColor: [15, 23, 42] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 42, fillColor: [248, 250, 252] }
    }
  });
  currentY = doc.lastAutoTable.finalY + 8;

  currentY = drawSectionTitle("Legal Terms", currentY);
  autoTable(doc, {
    startY: currentY,
    body: [
      ["Terms", (invoice.terms || INVOICE_BRAND.terms || []).slice(0, 3).join("\n") || "-"],
      ["Transport Regulations", (invoice.transportRegulations || INVOICE_BRAND.transportRegulations || []).slice(0, 2).join("\n") || "-"],
      ["Company Conditions", (invoice.companyTerms || INVOICE_BRAND.companyTerms || []).slice(0, 2).join("\n") || "-"]
    ],
    tableWidth: CONTENT_WIDTH,
    theme: "grid",
    styles: { fontSize: 8.2, cellPadding: 2.6, textColor: [15, 23, 42] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 46, fillColor: [248, 250, 252] }
    }
  });

  const footerY = PAGE_HEIGHT - 10;
  doc.setDrawColor(...accent);
  doc.line(MARGIN, footerY - 4, PAGE_WIDTH - MARGIN, footerY - 4);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...slate);
  doc.text(`${INVOICE_BRAND.companyName} | ${INVOICE_BRAND.contactEmail} | ${INVOICE_BRAND.contactPhone}`, MARGIN, footerY);
  doc.text(`Page 1`, PAGE_WIDTH - MARGIN, footerY, { align: "right" });

  doc.save(`Invoice-${Date.now()}.pdf`);
};